import { Capacitor, registerPlugin } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";

export type NativePushState = {
  subscriptionId?: string | null;
  token?: string | null;
  optedIn?: boolean;
  permission?: boolean;
  accepted?: boolean;
};

type SyncResult =
  | { synced: true; subscriptionId: string; state: NativePushState }
  | { synced: false; reason: string; state?: NativePushState };

interface NativePushBridgePlugin {
  getState(): Promise<NativePushState>;
  requestPermission(): Promise<NativePushState>;
  optIn(): Promise<NativePushState>;
  optOut(): Promise<NativePushState>;
  login(options: { externalId: string }): Promise<void>;
  logout(): Promise<void>;
}

const NativePushBridge = registerPlugin<NativePushBridgePlugin>("NativePushBridge");

/**
 * True only when JS is running inside the native Capacitor shell (or explicit ios/android).
 * Do NOT use `Capacitor.isPluginAvailable("NativePushBridge")` here: `registerPlugin` stubs
 * report as "available" on web, which would incorrectly skip OneSignal web push.
 */
function isCapacitorNativeRuntime(): boolean {
  if (Capacitor.isNativePlatform()) return true;
  const platform = Capacitor.getPlatform();
  return platform === "android" || platform === "ios";
}

export function isNativePushPlatform(): boolean {
  return isCapacitorNativeRuntime();
}

export async function detectNativePushBridge(): Promise<boolean> {
  if (!isCapacitorNativeRuntime()) {
    return false;
  }

  try {
    await NativePushBridge.getState();
    return true;
  } catch {
    // Bridge may not be ready on first paint; still treat as native so we don't fall back to web OneSignal.
    return true;
  }
}

function normalizeState(state: NativePushState | null | undefined): NativePushState {
  return {
    subscriptionId: state?.subscriptionId ? String(state.subscriptionId) : null,
    token: state?.token ? String(state.token) : null,
    optedIn: !!state?.optedIn,
    permission: !!state?.permission,
    accepted: typeof state?.accepted === "boolean" ? state.accepted : undefined,
  };
}

export async function getNativePushState(): Promise<NativePushState | null> {
  if (!isNativePushPlatform()) return null;
  return normalizeState(await NativePushBridge.getState());
}

export async function setNativePushExternalUser(externalId: string): Promise<void> {
  if (!isNativePushPlatform() || !externalId) return;
  await NativePushBridge.login({ externalId });
}

export async function clearNativePushExternalUser(): Promise<void> {
  if (!isNativePushPlatform()) return;
  await NativePushBridge.logout();
}

export async function requestNativePushPermission(): Promise<NativePushState | null> {
  if (!isNativePushPlatform()) return null;
  return normalizeState(await NativePushBridge.requestPermission());
}

export async function optInNativePush(): Promise<NativePushState | null> {
  if (!isNativePushPlatform()) return null;
  return normalizeState(await NativePushBridge.optIn());
}

export async function optOutNativePush(): Promise<NativePushState | null> {
  if (!isNativePushPlatform()) return null;
  return normalizeState(await NativePushBridge.optOut());
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function waitForNativeSubscriptionId(initialState: NativePushState | null): Promise<NativePushState | null> {
  let state = initialState;
  for (let attempt = 0; attempt < 10; attempt += 1) {
    if (String(state?.subscriptionId || "").trim()) {
      return state;
    }
    await sleep(750);
    state = await getNativePushState();
  }
  return state;
}

export async function syncNativePushSubscription(args: {
  businessId: string;
  userId: string;
  userAgent?: string;
  requestPermission?: boolean;
}): Promise<SyncResult> {
  if (!isNativePushPlatform()) {
    return { synced: false, reason: "not-native-platform" };
  }

  await setNativePushExternalUser(args.userId);

  let state = await getNativePushState();
  if (args.requestPermission && !state?.permission) {
    state = await requestNativePushPermission();
  }

  if (!state?.permission) {
    return { synced: false, reason: "permission-not-granted", state: state ?? undefined };
  }

  if (!state.optedIn) {
    state = await optInNativePush();
  }

  state = await waitForNativeSubscriptionId(state);

  const subscriptionId = String(state?.subscriptionId || "").trim();
  if (!subscriptionId) {
    return { synced: false, reason: "subscription-id-unavailable", state: state ?? undefined };
  }

  // Reinstall creates a new OneSignal subscription id — clear stale rows for this player id.
  await (supabase as any)
    .from("push_subscriptions")
    .delete()
    .eq("provider_subscription_id", subscriptionId)
    .neq("user_id", args.userId);

  const { error } = await (supabase as any)
    .from("push_subscriptions")
    .upsert(
      {
        business_id: args.businessId,
        user_id: args.userId,
        provider: "onesignal",
        provider_subscription_id: subscriptionId,
        user_agent: args.userAgent || navigator.userAgent,
      },
      { onConflict: "business_id,user_id" },
    );

  if (error) {
    throw error;
  }

  return {
    synced: true,
    subscriptionId,
    state: state ?? undefined,
  };
}

export async function deleteNativePushSubscription(args: {
  businessId: string;
  userId: string;
}): Promise<void> {
  if (isNativePushPlatform()) {
    await optOutNativePush();
  }

  const { error } = await (supabase as any)
    .from("push_subscriptions")
    .delete()
    .eq("business_id", args.businessId)
    .eq("user_id", args.userId);

  if (error) {
    throw error;
  }
}
